import { AddCircleOutline } from '@app/components/various/Icons';
import { Link } from '@app/components/various/Link';
import { isDefined } from '@app/utils/is';
import { Id, Lookbook } from '@typings';
import { nanoid } from 'nanoid';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ListItem } from '../AddedNavigationItems/ListItem';
import { useLookbookNavigationSettingsContext } from '../LookbookNavigationSettingsContext';

interface Props {
  pageId: Id;
}

export const AvailablePageItem = ({ pageId }: Props) => {
  const { addedMenuItems, pagesRecords, formMethods, scrollAddedMenuItems } = useLookbookNavigationSettingsContext();

  const { t } = useTranslation(['common', 'cms']);

  const page = pagesRecords[pageId];

  const handleAddMenuItem = React.useCallback(() => {
    if (!isDefined(page)) {
      return;
    }

    const newMenuItem: Lookbook.PageNavigationItem = {
      id: nanoid(),
      label: page.name,
      openNewTab: false,
      pageId,
      type: 'page',
      url: page.slug,
    };

    formMethods.setValue('navigationItems', [...addedMenuItems, newMenuItem], { shouldDirty: true });
    scrollAddedMenuItems();
  }, [page, pageId, formMethods, addedMenuItems, scrollAddedMenuItems]);

  if (!isDefined(page)) {
    return null;
  }

  return (
    <li className="relative rounded-regular border border-neutral-90 bg-white p-2 transition-colors focus-within:bg-neutral-98 hover:bg-neutral-98">
      <ListItem
        prefix={
          <button
            type="button"
            aria-label={t('common:add_value', { value: page.name })}
            className="rounded-full absolute-cursor-pointer"
            onClick={handleAddMenuItem}
          >
            <AddCircleOutline className="size-4" />
          </button>
        }
      >
        <Link
          size="medium"
          to={page.slug}
          target="_blank"
          aria-label={t('common:preview_value', { value: page.name })}
          className="z-elevated"
        >
          {page.slug}
        </Link>
      </ListItem>
    </li>
  );
};
